import { ADMIN_AUDIT_PAGE_SIZE } from '../../constants/adminAudit.js';

export { ADMIN_AUDIT_PAGE_SIZE };

function clampPageSize(pageSize) {
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? Math.floor(size) : ADMIN_AUDIT_PAGE_SIZE;
}

function optionalTrim(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * Inclusive UTC calendar-day bounds must stay YYYY-MM-DD.
 * @param {unknown} after
 * @param {unknown} before
 * @returns {boolean}
 */
export function isReversedAuditDateRange(after, before) {
  const createdAfter = optionalTrim(after);
  const createdBefore = optionalTrim(before);
  if (!createdAfter || !createdBefore) return false;
  return createdAfter > createdBefore;
}

/**
 * Builds a listAdminAuditLogs payload. At most one of action | actorUid | targetType.
 * Dates may combine with that single equality dimension.
 * @param {{
 *   action?: string,
 *   actorUid?: string,
 *   targetType?: string,
 *   createdAfter?: string,
 *   createdBefore?: string,
 *   pageSize?: number,
 *   pageToken?: string | null,
 * }} [params]
 */
export function buildAdminAuditRequest({
  action,
  actorUid,
  targetType,
  createdAfter,
  createdBefore,
  pageSize = ADMIN_AUDIT_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    pageSize: clampPageSize(pageSize),
  };

  const nextAction = optionalTrim(action);
  const nextActorUid = optionalTrim(actorUid);
  const nextTargetType = optionalTrim(targetType);
  const equalityCount = [nextAction, nextActorUid, nextTargetType].filter(Boolean).length;
  if (equalityCount > 1) {
    throw new Error('V1 accepts at most one of action, actorUid, or targetType.');
  }
  if (nextAction) request.action = nextAction;
  if (nextActorUid) request.actorUid = nextActorUid;
  if (nextTargetType) request.targetType = nextTargetType;

  const after = optionalTrim(createdAfter);
  const before = optionalTrim(createdBefore);
  if (after) request.createdAfter = after;
  if (before) request.createdBefore = before;
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

export function buildAuditBrowseRequest({
  pageSize = ADMIN_AUDIT_PAGE_SIZE,
  pageToken,
  createdAfter,
  createdBefore,
} = {}) {
  return buildAdminAuditRequest({ pageSize, pageToken, createdAfter, createdBefore });
}

export function buildAuditActionRequest({
  action,
  pageSize = ADMIN_AUDIT_PAGE_SIZE,
  pageToken,
  createdAfter,
  createdBefore,
} = {}) {
  return buildAdminAuditRequest({
    action,
    pageSize,
    pageToken,
    createdAfter,
    createdBefore,
  });
}

export function buildAuditActorRequest({
  actorUid,
  pageSize = ADMIN_AUDIT_PAGE_SIZE,
  pageToken,
  createdAfter,
  createdBefore,
} = {}) {
  return buildAdminAuditRequest({
    actorUid,
    pageSize,
    pageToken,
    createdAfter,
    createdBefore,
  });
}

export function buildAuditTargetTypeRequest({
  targetType,
  pageSize = ADMIN_AUDIT_PAGE_SIZE,
  pageToken,
  createdAfter,
  createdBefore,
} = {}) {
  return buildAdminAuditRequest({
    targetType,
    pageSize,
    pageToken,
    createdAfter,
    createdBefore,
  });
}
